// caiet UC21-08 — the Administrator Sistemului may inactivate and activate the
// credentials and the admitted IP addresses of a Sistem Registrator, but may not
// set or reset them: setting and resetting are UC31, the Administrator
// Registratorului's. This register is therefore read plus two switches.
// per docs/shape-uc-31-system-credentials.md §Addendum 2026-08-28 (credential lifecycle)
// per docs/shape-uc-31-credential-history.md §Approach
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import {
  MudButton,
  MudModal,
  MudInfoBox,
  MudSelect,
  MudSwitch,
  MudTable,
  MudTag,
} from '@egov-moldova/mud-react';
import {
  type ActiveFilter,
  EmptyRegisterMessage,
  FilterBar,
  formatDateTime,
  onModalDismiss,
  onRowOpen,
  PageHeader,
  RowActionsMenu,
} from '@simsm/ui-shared';
import { apiErrorKey } from '../../app/api-client';
import { QueryState } from '../../components/QueryState/QueryState';
import { CredentialStatusTag } from '../../features/credentials/CredentialStatusTag';
import {
  admittedAddresses,
  listCredentials,
  setAddressActive,
  setCredentialActive,
  type SystemCredential,
} from '../../features/credentials/api';
import { listRegistrars, type RegistrarRow } from '../../features/registrars/api';
import { CredentialHistoryModal } from './CredentialHistory';
import { HistoryActor } from '../../features/history/CatalogHistoryLog';

type StatusFilter = '' | 'active' | 'inactive' | 'unset';

// mud-table's own column type is not re-exported from the package root.
interface TableColumn {
  key: string;
  label: string;
  width?: string;
}

interface RegisterRow {
  registrar: RegistrarRow;
  credential: SystemCredential | null;
}

function matchesStatus(row: RegisterRow, filter: StatusFilter): boolean {
  if (filter === '') return true;
  if (filter === 'unset') return row.credential === null;
  return row.credential !== null && (filter === 'active') === row.credential.active;
}

export function SystemCredentialsRegisterPage(): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // The Registrar register links here scoped to one entity
  // (AdminRegistratori.tsx:390-424 "Credențiale sistem"), so `?registrar=`
  // opens the page already narrowed to it. `typed === null` means the
  // administrator has not touched the box yet; the moment they do — including
  // clearing it — what they typed wins over the address.
  const [params] = useSearchParams();
  const [typed, setTyped] = useState<string | null>(null);
  const search = typed ?? params.get('registrar') ?? '';
  const setSearch = setTyped;
  const [status, setStatus] = useState<StatusFilter>('');
  const [confirmOff, setConfirmOff] = useState<RegisterRow | null>(null);
  const [addressesOf, setAddressesOf] = useState<RegisterRow | null>(null);
  const [historyOf, setHistoryOf] = useState<RegisterRow | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  // The search term gets no chip: it clears from the box's own ×, which is the
  // wireframe's own rule for the shared bar (BaraFiltre.tsx:35).
  const activeFilters: ActiveFilter[] = status
    ? [
        {
          key: 'status',
          label: t('credentials.register.filter.status'),
          value: t(`credentials.status.${status}`),
          onRemove: () => setStatus(''),
        },
      ]
    : [];

  // Two reads, one table: the register is every Registrar (including those with
  // no Sistem Registrator yet), and the credential state comes from the auth side.
  const [registrars, credentials] = useQueries({
    queries: [
      { queryKey: ['admin-registrars', search], queryFn: () => listRegistrars({ q: search }) },
      { queryKey: ['admin-credentials', 'all'], queryFn: () => listCredentials() },
    ],
  });

  const rows = useMemo<RegisterRow[]>(() => {
    const byRegistrar = new Map((credentials.data ?? []).map((c) => [c.registrarId, c]));
    return (registrars.data ?? []).map((registrar) => ({
      registrar,
      credential: byRegistrar.get(registrar.idno) ?? null,
    }));
  }, [registrars.data, credentials.data]);

  const visible = rows.filter((row) => matchesStatus(row, status));

  function refresh(): void {
    void queryClient.invalidateQueries({ queryKey: ['admin-credentials'] });
  }

  const toggle = useMutation({
    mutationFn: (input: { credential: SystemCredential; active: boolean }) =>
      setCredentialActive({
        clientId: input.credential.clientId,
        registrarId: input.credential.registrarId,
        active: input.active,
      }),
    onSuccess: () => {
      setConfirmOff(null);
      setFailure(null);
      refresh();
    },
    // The register's only refusal that is not a bug is REGISTRAR_INACTIVE, and
    // it can still arrive on a screen whose menu offered the action: the list is
    // cached, and the Registrar may have been inactivated in another tab.
    // Saying so beats a silent no-op (Constitution IV).
    onError: (err: unknown) => setFailure(t(apiErrorKey(err, 'credentials.error.toggle_failed'))),
  });

  const toggleAddress = useMutation({
    mutationFn: (input: { credential: SystemCredential; address: string; active: boolean }) =>
      setAddressActive({
        clientId: input.credential.clientId,
        registrarId: input.credential.registrarId,
        address: input.address,
        active: input.active,
      }),
    onSuccess: (credential) => {
      setAddressesOf((current) => (current ? { ...current, credential } : current));
      setFailure(null);
      refresh();
    },
    onError: (err: unknown) =>
      setFailure(t(apiErrorKey(err, 'credentials.error.ip_toggle_failed'))),
  });

  const modalAddresses = addressesOf?.credential ? admittedAddresses(addressesOf.credential) : [];

  // The wireframe's table (CredentialeAdmin.tsx `leading-actions-table`): the
  // contextual menu first, then the same five columns.
  const columns: TableColumn[] = [
    { key: 'actions', label: t('credentials.register.actions'), width: '56px' },
    { key: 'registrar', label: t('credentials.register.column.registrar') },
    { key: 'status', label: t('credentials.register.column.status') },
    { key: 'clientId', label: t('credentials.register.column.client_id') },
    { key: 'ips', label: t('credentials.register.column.ips') },
    { key: 'updated', label: t('credentials.register.column.updated') },
  ];
  const tableRows = visible.map((row) => {
    const addressCount = row.credential ? admittedAddresses(row.credential).length : 0;
    return {
      id: row.registrar.idno,
      actions: '',
      registrar: row.registrar.name,
      status: row.credential === null ? t('credentials.status.unset') : '',
      clientId: row.credential?.clientId ?? t('credentials.register.none'),
      ips:
        addressCount === 1
          ? t('credentials.register.ips_count_one')
          : t('credentials.register.ips_count_other', { count: addressCount }),
      updated: row.credential?.updatedAtIso
        ? formatDateTime(row.credential.updatedAtIso)
        : t('credentials.register.none'),
    };
  });
  const addressColumns: TableColumn[] = [
    { key: 'address', label: t('credentials.ips.column.address') },
    { key: 'format', label: t('credentials.ips.column.format') },
    { key: 'state', label: t('credentials.ips.column.state') },
  ];

  const combined = {
    isError: registrars.isError || credentials.isError,
    isLoading: registrars.isLoading || credentials.isLoading,
    data: registrars.data === undefined ? undefined : rows,
    error: registrars.error ?? credentials.error,
    refetch: () => {
      void registrars.refetch();
      void credentials.refetch();
    },
  };

  // An empty register keeps its table. The wireframe leaves the head, the
  // frame and the footer standing and puts the message in one centred cell
  // spanning every column (CredentialeAdmin.tsx:89); a box in
  // place of the register hides what the register would even hold. Both
  // QueryState branches render this same node, because mud-table draws its
  // `empty` slot on its own once `rows` is [].
  const register = (
    <div className="simsm-syscred__table">
      <MudTable
        className="simsm-table"
        columns={columns}
        rows={tableRows}
        rowStyle="zebra"
        disableSort={true}
        aria-label={t('credentials.register.table.aria_label')}
        // The whole row opens what the menu's first entry opens: the
        // Registrar's system tab (CredentialeAdmin.tsx:73 `tr.clickable`).
        hoverable
        onMudRowClick={onRowOpen((index) => {
          const row = visible[index];
          if (row) navigate(`/system-admin/registrars/${row.registrar.idno}?tab=system`);
        })}
      >
        <EmptyRegisterMessage>{t('credentials.register.empty')}</EmptyRegisterMessage>
        {/* No visible heading over the menu column, the wireframe's
            `<th className="leading-actions-column" aria-label="Acțiuni" />`;
            the slotted, visually hidden text names it for assistive
            technology. */}
        <span slot="header-cell-actions" className="visually-hidden">
          {t('credentials.register.actions')}
        </span>
        {visible.map((row, index) => {
          const addressCount = row.credential ? admittedAddresses(row.credential).length : 0;
          // caiet UC21-07/UC21-08, and the wireframe's own rule
          // (CredentialeAdmin.tsx:76): while the Registrar is inactive its
          // credentials stay inactive, so the activation is refused rather
          // than offered. The kill switch — deactivation — is never blocked.
          const registrarInactive = !row.registrar.active;
          const canActivate = row.credential !== null && !registrarInactive;
          // CF-48: the action is the System Administrator's own block. A block the
          // Registrar Administrator set is shown on the tag and is not this
          // action's to lift.
          const activating = row.credential?.blockedBySystemAdmin === true;
          // caiet UC21-07: an inactive Registrar's data reads as inactive
          // (wireframe CredentialeAdmin.tsx:73 `tr.inactiv`). The name and
          // the tag keep their own colour: what is dimmed is the data, not
          // the identity.
          const muted = registrarInactive ? 'simsm-syscred__cell--inactive' : '';
          const key = row.registrar.idno;
          return [
            <span key={`actions-${key}`} slot={`cell-actions-${index}`}>
              <RowActionsMenu
                label={t('credentials.register.actions_for', {
                  name: row.registrar.name,
                })}
                actions={[
                  {
                    label: t('credentials.register.action.open_system'),
                    onSelect: () =>
                      navigate(`/system-admin/registrars/${row.registrar.idno}?tab=system`),
                  },
                  {
                    label: activating
                      ? t('credentials.register.action.activate')
                      : t('credentials.register.action.deactivate'),
                    danger: !activating,
                    disabled: activating
                      ? !canActivate || toggle.isPending
                      : row.credential === null || toggle.isPending,
                    reason:
                      row.credential === null
                        ? t('credentials.register.reason.unset')
                        : t('credentials.register.reason.registrar_inactive'),
                    onSelect: () => {
                      if (!row.credential) return;
                      if (!activating) {
                        setConfirmOff(row);
                        return;
                      }
                      toggle.mutate({ credential: row.credential, active: true });
                    },
                  },
                  {
                    label: t('credentials.register.action.ips'),
                    disabled: addressCount === 0,
                    reason: t('credentials.register.reason.no_ips'),
                    onSelect: () => setAddressesOf(row),
                  },
                  {
                    label: t('history.action.open'),
                    disabled: row.credential === null,
                    reason: t('credentials.register.reason.unset'),
                    onSelect: () => setHistoryOf(row),
                  },
                  {
                    label: t('credentials.register.action.file'),
                    separator: true,
                    onSelect: () => navigate(`/system-admin/registrars/${row.registrar.idno}`),
                  },
                ]}
              />
            </span>,
            <span
              key={`registrar-${key}`}
              slot={`cell-registrar-${index}`}
              className="simsm-syscred__cell--stack"
            >
              <span className="simsm-syscred__name">
                {row.registrar.name}
                {registrarInactive ? (
                  <MudTag
                    variant="status"
                    type="subtle"
                    semantic="neutral"
                    label={t('credentials.register.registrar_inactive')}
                  />
                ) : null}
              </span>
              <span className="simsm-syscred__meta">
                {row.registrar.kind
                  ? t('credentials.register.meta', {
                      idno: row.registrar.idno,
                      kind: t(`registrars.kind.${row.registrar.kind}`),
                    })
                  : t('credentials.register.meta_idno', { idno: row.registrar.idno })}
              </span>
            </span>,
            <span key={`status-${key}`} slot={`cell-status-${index}`}>
              <CredentialStatusTag
                configured={row.credential !== null}
                active={row.credential?.active ?? false}
                blockedBySystemAdmin={row.credential?.blockedBySystemAdmin ?? false}
                blockedByRegistrar={row.credential?.blockedByRegistrar ?? false}
                viewer="SYSTEM_ADMIN"
              />
            </span>,
            <span
              key={`client-${key}`}
              slot={`cell-clientId-${index}`}
              className={`simsm-syscred__mono simsm-syscred__mono--id ${muted}`}
            >
              {row.credential ? (
                row.credential.clientId
              ) : (
                <span className="simsm-syscred__muted">{t('credentials.register.none')}</span>
              )}
            </span>,
            <span key={`ips-${key}`} slot={`cell-ips-${index}`} className={muted}>
              {addressCount === 1
                ? t('credentials.register.ips_count_one')
                : t('credentials.register.ips_count_other', { count: addressCount })}
            </span>,
            /* The wireframe pairs the stamp with who made the change
               (CredentialeAdmin.tsx:87, "Modificat de"). A time on its own
               says a change happened and refuses to say whose it was,
               while the credential trail beside it already knew. The
               actor comes from the newest trail row, written in the same
               transaction as the change, so the two cannot disagree —
               and it is rendered by the same three-state renderer as
               every other history surface, so a credential older than the
               trail reads "not recorded" instead of "Sistem". */
            <span
              key={`updated-${key}`}
              slot={`cell-updated-${index}`}
              className={`simsm-syscred__cell--stack ${muted}`}
            >
              {row.credential?.updatedAtIso ? (
                <>
                  <span>{formatDateTime(row.credential.updatedAtIso)}</span>
                  <span className="simsm-syscred__meta">
                    {t('credentials.register.changed_by')}{' '}
                    <HistoryActor
                      entry={{
                        actorId: row.credential.lastChangeActorId,
                        actorRole: row.credential.lastChangeActorRole,
                      }}
                    />
                  </span>
                </>
              ) : (
                <span className="simsm-syscred__muted">{t('credentials.register.none')}</span>
              )}
            </span>,
          ];
        })}
      </MudTable>
    </div>
  );

  return (
    <section className="simsm-page simsm-syscred">
      <PageHeader title={t('credentials.register.title')} lede={t('credentials.register.lede')} />

      {failure ? (
        <MudInfoBox emphasis="strong" role="alert" variant="error">
          {failure}
        </MudInfoBox>
      ) : null}

      <FilterBar
        search={{
          value: search,
          onChange: setSearch,
          placeholder: t('credentials.register.search.placeholder'),
          ariaLabel: t('credentials.register.search.label'),
          clearLabel: t('common.filter.clear_search'),
        }}
        active={activeFilters}
        onReset={() => {
          setSearch('');
          setStatus('');
        }}
        labels={{
          filters: t('common.filter.button'),
          reset: t('common.filter.reset'),
          panelAriaLabel: t('credentials.register.filter.aria_label'),
          activeAriaLabel: t('common.filter.active_aria_label'),
        }}
        panel={
          <label className="simsm-filter-bar__field">
            <span className="simsm-filter-bar__field-label">
              {t('credentials.register.filter.status')}
            </span>
            <MudSelect
              size="medium"
              value={status}
              aria-label={t('credentials.register.filter.status')}
              onMudChange={(e: CustomEvent<{ value: string }>) =>
                setStatus(e.detail.value as StatusFilter)
              }
            >
              <option value="">{t('credentials.register.filter.all')}</option>
              <option value="active">{t('credentials.status.active')}</option>
              <option value="inactive">{t('credentials.status.inactive')}</option>
              <option value="unset">{t('credentials.status.unset')}</option>
            </MudSelect>
          </label>
        }
      />

      <QueryState query={combined} isEmpty={visible.length === 0} empty={register}>
        {register}
      </QueryState>

      {combined.data === undefined ? null : (
        <p className="simsm-syscred__count">
          {visible.length === 1
            ? t('credentials.register.count_one')
            : t('credentials.register.count_other', { count: visible.length })}
          {visible.length === rows.length
            ? ''
            : ` ${t('credentials.register.count_of', { total: rows.length })}`}
        </p>
      )}

      <MudModal
        closeLabel={t('common.close')}
        open={confirmOff !== null}
        size="md"
        titleText={t('credentials.register.deactivate.title')}
        // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — seven AGE
        // components emit `mudClose` bubbling+composed, so a descendant closing inside this
        // modal reaches this handler too; the guard answers only a real dismissal.
        onMudClose={onModalDismiss(() => setConfirmOff(null))}
      >
        <MudInfoBox emphasis="strong" variant="warning">
          {t('credentials.register.deactivate.body', {
            name: confirmOff?.registrar.name ?? '',
          })}
        </MudInfoBox>
        <div slot="actions" className="simsm-syscred__modal-actions">
          <MudButton
            shape="circular"
            variant="neutral"
            size="md"
            onClick={() => setConfirmOff(null)}
          >
            {t('credentials.common.cancel')}
          </MudButton>
          <MudButton
            shape="circular"
            variant="destructive"
            size="md"
            disabled={toggle.isPending}
            onClick={() => {
              if (confirmOff?.credential) {
                toggle.mutate({ credential: confirmOff.credential, active: false });
              }
            }}
          >
            {t('credentials.action.deactivate')}
          </MudButton>
        </div>
      </MudModal>

      {historyOf?.credential ? (
        <CredentialHistoryModal
          onClose={() => setHistoryOf(null)}
          registrarName={historyOf.registrar.name}
          clientId={historyOf.credential.clientId}
          registrarId={historyOf.credential.registrarId}
        />
      ) : null}

      <MudModal
        closeLabel={t('common.close')}
        open={addressesOf !== null}
        size="md"
        titleText={t('credentials.register.ips.title')}
        // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — seven AGE
        // components emit `mudClose` bubbling+composed, so a descendant closing inside this
        // modal reaches this handler too; the guard answers only a real dismissal.
        onMudClose={onModalDismiss(() => setAddressesOf(null))}
      >
        <div className="simsm-syscred__modal-body">
          <p className="simsm-syscred__card-lede">{addressesOf?.registrar.name ?? ''}</p>
          <div className="simsm-syscred__table">
            <MudTable
              className="simsm-table"
              columns={addressColumns}
              rows={modalAddresses.map((entry) => ({
                id: entry.address,
                address: entry.address,
                format: entry.format,
                state: entry.blockedBySystemAdmin
                  ? t('credentials.ips.state.inactive')
                  : entry.blockedByRegistrar
                    ? t('credentials.ips.state.blocked_by_registrar')
                    : t('credentials.ips.state.active'),
              }))}
              rowStyle="divided"
              disableSort={true}
              aria-label={t('credentials.ips.table.aria_label')}
            >
              {modalAddresses.map((entry, index) => [
                <span
                  key={`address-${entry.address}`}
                  slot={`cell-address-${index}`}
                  className="simsm-syscred__mono"
                >
                  {entry.address}
                </span>,
                <span
                  key={`state-${entry.address}`}
                  slot={`cell-state-${index}`}
                  className="simsm-syscred__ip-state"
                >
                  {/* The words go in the `label` slot: the prop is an
                      accessible-name fallback that renders no visible text
                      (mud-switch), and the wireframe puts the state beside the
                      control (CredentialeAdmin.tsx:130). CF-48: the switch is
                      the System Administrator's own block; the Registrar
                      Administrator's is named beside it and not lifted here. */}
                  <MudSwitch
                    checked={!entry.blockedBySystemAdmin}
                    disabled={toggleAddress.isPending}
                    aria-label={t('credentials.register.ips.toggle', { address: entry.address })}
                    onMudChange={() => {
                      if (!addressesOf?.credential) return;
                      toggleAddress.mutate({
                        credential: addressesOf.credential,
                        address: entry.address,
                        active: entry.blockedBySystemAdmin,
                      });
                    }}
                  >
                    <span slot="label">
                      {entry.blockedBySystemAdmin
                        ? t('credentials.ips.state.inactive')
                        : t('credentials.ips.state.active')}
                    </span>
                  </MudSwitch>
                  {entry.blockedByRegistrar ? (
                    <MudTag
                      variant="status"
                      type="subtle"
                      size="sm"
                      semantic="accent"
                      label={t('credentials.ips.state.blocked_by_registrar')}
                    />
                  ) : null}
                </span>,
              ])}
            </MudTable>
          </div>
          <p className="simsm-syscred__hint">{t('credentials.register.ips.note')}</p>
        </div>
        <div slot="actions" className="simsm-syscred__modal-actions">
          <MudButton
            shape="circular"
            variant="neutral"
            size="md"
            onClick={() => setAddressesOf(null)}
          >
            {t('credentials.common.close')}
          </MudButton>
        </div>
      </MudModal>
    </section>
  );
}
